/**
 * Market data API service.
 *
 * Paths omit the `/api` prefix (the base URL already includes `/api` via
 * runtime config), matching the other frontend services.
 */

import type { ImpliedVolatility } from '@covered-calls/shared';
import { apiClient } from './api-client';

/**
 * Raw shape returned by GET /api/market/iv/{ticker}. The backend returns the
 * IV under `impliedVolatility` (number | null), NOT under `value`, so it must
 * be mapped to the shared `ImpliedVolatility` type at this boundary.
 */
interface RawIvResponse {
  ticker?: string;
  strike?: number;
  expiry?: string;
  impliedVolatility: number | null;
  message?: string;
}

/**
 * Fetch implied volatility for a ticker (optionally for a specific strike and
 * expiry). Returns a typed `ImpliedVolatility` when a numeric IV is available,
 * or `null` when the provider has no IV for the request.
 */
export async function getImpliedVolatility(
  ticker: string,
  params?: { strike?: number; expiry?: string },
  options?: { signal?: AbortSignal },
): Promise<ImpliedVolatility | null> {
  const query = new URLSearchParams();
  if (params?.strike !== undefined) query.set('strike', String(params.strike));
  if (params?.expiry) query.set('expiry', params.expiry);
  const qs = query.toString();
  const path = `/market/iv/${encodeURIComponent(ticker)}${qs ? `?${qs}` : ''}`;

  const raw = await apiClient.get<RawIvResponse>(path, options);
  if (!raw || typeof raw.impliedVolatility !== 'number') {
    return null;
  }
  return { value: raw.impliedVolatility, timestamp: new Date().toISOString() };
}
